import axios from 'axios';
import SessionService from './SessionService';

const UNOFFICIAL_API_URL = process.env.REACT_APP_UNOFFICIAL_API_URL || 'https://server.mends.pro/api/v1';

export interface UnofficialMessageResponse {
  status: string;
  message: string;
  data: {
    recipient: string;
    message?: string;
    fileName?: string;
    mediaType?: string;
  };
  timestamp: string;
}

export interface SendTextMessageDTO {
  sectorId: number;
  recipient: string;
  message: string;
  contactId?: number;
  userId?: number;
  isAnonymous?: boolean;
  sentAt?: string;
}

export interface SendImageDTO {
  base64File: string;
  fileName: string;
  mediaType: string;
  recipient: string;
  sectorId: number;
  caption?: string;
  userId?: number;
  isAnonymous?: boolean;
  sentAt?: string;
}

export interface SendDocumentDTO {
  base64File: string;
  fileName: string;
  mediaType: string;
  recipient: string;
  sectorId: number;
  caption?: string;
  userId?: number;
  isAnonymous?: boolean;
  sentAt?: string;
}

export interface SendMediaMessageDTO {
  base64File: string;
  fileName: string;
  mediaType: string;
  recipient: string;
  sectorId: number;
  caption?: string;
  userId?: number;
  isAnonymous?: boolean;
  sentAt?: string;
  contactId?: number;
}

export const sendTextMessage = async (data: SendTextMessageDTO): Promise<UnofficialMessageResponse> => {
  try {
    const payload = {
      ...data,
      sentAt: data.sentAt || new Date().toISOString()
    };
    const response = await axios.post<UnofficialMessageResponse>(`${UNOFFICIAL_API_URL}/send-message`, payload, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SessionService.getToken()}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error sending unofficial text message:', error);
    throw error;
  }
};

export const sendImage = async (data: SendImageDTO): Promise<UnofficialMessageResponse> => {
  try {
    const payload = {
      base64File: data.base64File,
      fileName: data.fileName,
      mediaType: data.mediaType,
      recipient: data.recipient,
      sectorId: data.sectorId,
      caption: data.caption || '',
      userId: data.userId,
      isAnonymous: data.isAnonymous,
      sentAt: data.sentAt || new Date().toISOString()
    };

    const response = await axios.post<UnofficialMessageResponse>(`${UNOFFICIAL_API_URL}/send-image`, payload, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SessionService.getToken()}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error sending unofficial image:', error);
    throw error;
  }
};

export const sendAudio = async (data: SendMediaMessageDTO): Promise<UnofficialMessageResponse> => {
  try {
    // Garantir que o base64 não tenha o prefixo data:audio
    const base64File = data.base64File.replace(/^data:audio\/(wav|mpeg|mp3|ogg);base64,/, '');

    const payload = {
      base64File,
      fileName: data.fileName,
      mediaType: 'audio/wav', // Forçar o tipo como WAV
      recipient: data.recipient,
      sectorId: data.sectorId,
      caption: data.caption || '',
      userId: data.userId,
      isAnonymous: data.isAnonymous,
      sentAt: data.sentAt || new Date().toISOString(),
      ...(data.contactId != null ? { contactId: data.contactId } : {})
    };

    console.log('Enviando áudio para API:', {
      fileName: payload.fileName,
      mediaType: payload.mediaType,
      recipientLength: payload.recipient.length,
      base64Length: payload.base64File.length
    });

    const response = await axios.post<UnofficialMessageResponse>(`${UNOFFICIAL_API_URL}/send-audio`, payload, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SessionService.getToken()}`
      }
    });

    console.log('Resposta da API de áudio:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error sending unofficial audio:', error);
    throw error;
  }
};

export const sendDocument = async (data: SendDocumentDTO): Promise<UnofficialMessageResponse> => {
  try {
    const payload = {
      base64File: data.base64File,
      fileName: data.fileName,
      mediaType: data.mediaType,
      recipient: data.recipient,
      sectorId: data.sectorId,
      caption: data.caption || '',
      userId: data.userId,
      isAnonymous: data.isAnonymous,
      sentAt: data.sentAt || new Date().toISOString()
    };

    const response = await axios.post<UnofficialMessageResponse>(`${UNOFFICIAL_API_URL}/send-document`, payload, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SessionService.getToken()}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error sending unofficial document:', error);
    throw error;
  }
};

// Função auxiliar para converter File/Blob para Base64
export const fileToBase64 = (file: File | Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      }
    };
    reader.onerror = error => reject(error);
  });
};

// Send reaction/emote for 1:1 or group (calls unofficial/v1 backend if present)
export interface SendReactionDTO {
  sectorId: number;
  recipient: string;
  emoji: string;
  messageId?: string;
  userId?: number;
  isAnonymous?: boolean;
  sentAt?: string;
}

export const sendReaction = async (data: SendReactionDTO): Promise<UnofficialMessageResponse> => {
  try {
    const payload = {
      ...data,
      sentAt: data.sentAt || new Date().toISOString()
    };
    const response = await axios.post<UnofficialMessageResponse>(`${UNOFFICIAL_API_URL}/send-reaction`, payload, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SessionService.getToken()}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error sending unofficial reaction:', error);
    throw error;
  }
}; 